/** Immutable updates to trigger / LLM / nested-loop nodes inside a workflow document. */

import {
  isModelEngine,
  isTriggerEngine
} from 'src/content/Workflows/canvas/workflowNodeEngine';

import {
  applyOutputRefRenames,
  collectFieldNameRenames,
  collectStructuredFieldRenames
} from './renameOutputReferences';
import type {
  TemporalWorkflowDocument,
  WorkflowNodeDef
} from './workflowTypes';

export function cloneWorkflowDoc(
  doc: TemporalWorkflowDocument
): TemporalWorkflowDocument {
  return JSON.parse(JSON.stringify(doc)) as TemporalWorkflowDocument;
}

export type StructuredFieldRow = {
  description: string;
  type: string;
  required?: boolean;
};

export type StructuredFields = {
  [key: string]: StructuredFieldRow;
};

export const STRUCTURED_FIELD_TYPES = [
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object'
] as const;

export function updateTriggerNode(
  doc: TemporalWorkflowDocument,
  nodeId: string,
  patch: { fields?: any; label?: string; pdfPath?: string }
): TemporalWorkflowDocument {
  const next = cloneWorkflowDoc(doc);
  const n = next.nodes.find((x) => x.id === nodeId && isTriggerEngine(x.type));
  if (!n) {
    return next;
  }
  if (patch.label !== undefined) {
    n.label = patch.label;
  }
  if (patch.pdfPath !== undefined) {
    n.config = { ...n.config, pdf_path: patch.pdfPath };
    const fields = Array.isArray(n.config.fields)
      ? ([...(n.config.fields as object[])] as Record<string, unknown>[])
      : [];
    const idx = fields.findIndex(
      (f: Record<string, unknown>) => f.name === 'pdf_path' || f.type === 'file'
    );
    if (idx >= 0) {
      fields[idx] = { ...fields[idx], default: patch.pdfPath };
      n.config.fields = fields;
    }
  }
  if (patch.fields !== undefined) {
    const renames = collectFieldNameRenames(n.config.fields, patch.fields);
    n.config = {
      ...n.config,
      fields: patch.fields
    };
    return applyOutputRefRenames(next, nodeId, renames);
  }
  return next;
}

export function updateRootLlmNode(
  doc: TemporalWorkflowDocument,
  nodeId: string,
  patch: { label?: string; config: Record<string, unknown> }
): TemporalWorkflowDocument {
  const next = cloneWorkflowDoc(doc);
  const n = next.nodes.find((x) => x.id === nodeId && isModelEngine(x.type));
  if (!n) {
    return next;
  }
  if (patch.label !== undefined) {
    n.label = patch.label;
  }
  const renames = collectStructuredFieldRenames(
    n.config.structured_fields,
    patch.config.structured_fields
  );
  n.config = { ...n.config, ...patch.config };
  if (patch.config.output_mode === 'unstructured') {
    n.config.structured_fields = {} as StructuredFields;
  }
  return applyOutputRefRenames(next, nodeId, renames, 'response');
}

type LoopBody = { nodes: WorkflowNodeDef[]; edges: unknown[] };

export function updateLlmInsideLoop(
  doc: TemporalWorkflowDocument,
  loopId: string,
  innerNodeId: string,
  patch: { label?: string; config: Record<string, unknown> }
): TemporalWorkflowDocument {
  const next = cloneWorkflowDoc(doc);
  const loop = next.nodes.find((x) => x.id === loopId && x.type === 'loop');
  if (!loop?.config?.body) {
    return next;
  }
  const body = loop.config.body as LoopBody;
  const nodes = [...body.nodes];
  const idx = nodes.findIndex((x) => x.id === innerNodeId);
  if (idx < 0) {
    return next;
  }
  const inner = { ...nodes[idx] };
  if (patch.label !== undefined) {
    inner.label = patch.label;
  }
  const renames = collectStructuredFieldRenames(
    inner.config?.structured_fields,
    patch.config.structured_fields
  );
  inner.config = { ...inner.config, ...patch.config };
  if (patch.config.output_mode === 'unstructured') {
    inner.config.structured_fields = {} as StructuredFields;
  }
  nodes[idx] = inner;
  loop.config = {
    ...loop.config,
    body: { ...body, nodes }
  };
  return applyOutputRefRenames(next, innerNodeId, renames, 'response');
}

export function getNestedLlmNode(
  doc: TemporalWorkflowDocument,
  loopId: string,
  innerNodeId: string
): WorkflowNodeDef | null {
  const loop = doc.nodes.find((x) => x.id === loopId && x.type === 'loop');
  const body = loop?.config?.body as LoopBody | undefined;
  const inner = body?.nodes?.find((x) => x.id === innerNodeId);
  return inner && isModelEngine(inner.type) ? inner : null;
}

export function getLoopInnerAgents(
  doc: TemporalWorkflowDocument,
  loopId: string
): { id: string; label: string }[] {
  const loop = doc.nodes.find((x) => x.id === loopId && x.type === 'loop');
  const body = loop?.config?.body as LoopBody | undefined;
  if (!body?.nodes) {
    return [];
  }
  return body.nodes
    .filter((n) => isModelEngine(n.type))
    .map((n) => ({ id: n.id, label: n.label }));
}
